import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { useSelector } from "react-redux";
import configServ from "../services/config";
import toast from "react-hot-toast";
import UpdateIcon from "@mui/icons-material/Update";
const LeaveCard = ({
  leaveTypes = [],
  leaveList = [],
  setIschange,
  isEdit,
  setIsEdit,
  editData,
  isCreate,
  setIsCreate,
  setEditData,
  ischange,
}) => {
  const { company_id, is_super, is_admin, permissions, user_id } = useSelector(
    (state) => state.GlobalVariables,
  );
  const [form, setForm] = useState({
    leave_type_id: "",
    from_date: "",
    to_date: "",
    reason: "",
    leave_list_id: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 👉 If holiday selected → auto set date
    if (name === "leave_list_id") {
      const selectedHoliday = leaveList.find((item) => item.id === value);

      const holidayDate = selectedHoliday?.holiday_date
        ? selectedHoliday.holiday_date.split("T")[0]
        : "";

      setForm((prev) => ({
        ...prev,
        leave_list_id: value,
        to_date: holidayDate, // ✅ auto fill
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedLeave = leaveTypes.find(
    (item) => item.id === form.leave_type_id,
  );

  const isRH = selectedLeave?.leave_code === "RH";

  // ✅ Reset
  const handleReset = () => {
    setForm({
      leave_type_id: "",
      from_date: "",
      to_date: "",
      reason: "",
      leave_list_id: "",
    });
    setIsEdit(false);
    setEditData({});
  };

  const validate = () => {
    let newErrors = {};

    if (!form.leave_type_id) {
      newErrors.leave_type_id = "Leave type is required";
    }

    if (isRH) {
      // 👉 RH case
      if (!form.leave_list_id) {
        newErrors.leave_list_id = "Holiday is required";
      }

      if (!form.to_date) {
        newErrors.to_date = "To date is required";
      }
    } else {
      // 👉 Normal leave
      if (!form.from_date) {
        newErrors.from_date = "From date is required";
      }

      if (!form.to_date) {
        newErrors.to_date = "To date is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Submit
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      company_id: company_id,
      employee_id: user_id,
      ...form,
    };

    try {
      let response;

      if (isEdit) {
        // ✅ Update
        response = await configServ.updateLeave(editData.id, payload);
      } else {
        // ✅ Create
        response = await configServ.createLeave(payload);
      }

      if (response?.status) {
        toast.success(
          isEdit ? "Leave updated successfully" : "Leave created successfully",
        );

        handleReset(); // optional reset
        setIsCreate(false);
        setIschange(!ischange);
      } else {
        toast.error(response?.message || "Something went wrong");
      }

      console.log("Form Data 👉", payload);
    } catch (error) {
      console.error(error);
      toast.error("Server error");
    }
  };

  useEffect(() => {
    if (editData) {
      setForm({
        leave_type_id: editData.leave_type_id || "",
        from_date: editData.from_date || "",
        to_date: editData.to_date || "",
        reason: editData.reason || "",
        leave_list_id: editData.leave_list_id || "",
      });
    }
  }, [editData]);

  return (
    <motion.div
      style={{
        width: "100%",
        backgroundColor: "#e3f3fc",
        padding: 20,
        borderRadius: "10px",
        margin: "10px",
      }}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Grid container spacing={2}>
        {/* Leave Type */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Leave Type</InputLabel>
            <Select
              name="leave_type_id"
              value={form.leave_type_id}
              label="Leave Type"
              onChange={handleChange}
              sx={{ backgroundColor: "white" }}
              error={!!errors.leave_type_id}
              helperText={errors.leave_type_id}
            >
              <MenuItem value="">Select</MenuItem>
              {leaveTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.leave_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* RH → Holiday List */}
        {isRH && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Holiday</InputLabel>
              <Select
                name="leave_list_id"
                value={form.leave_list_id}
                label="Holiday"
                onChange={handleChange}
                sx={{ backgroundColor: "white" }}
                error={!!errors.leave_list_id}
                helperText={errors.leave_list_id}
              >
                <MenuItem value="">Select</MenuItem>
                {leaveList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* From Date (NOT for RH) */}
        {!isRH && (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              label="From Date"
              name="from_date"
              value={form.from_date}
              onChange={handleChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "white" }}
              error={!!errors.from_date}
              helperText={errors.from_date}
            />
          </Grid>
        )}

        {/* To Date */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            label="To Date"
            name="to_date"
            value={form.to_date}
            onChange={handleChange}
            fullWidth
            size="small"
            disabled={isRH} // ✅ disable when RH
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: "white" }}
            error={!!errors.to_date}
            helperText={errors.to_date}
          />
        </Grid>

        {/* Reason */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Reason"
            name="reason"
            value={form.reason}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ backgroundColor: "white" }}
          />
        </Grid>
      </Grid>

      {/* 🔥 Header with Buttons */}
      <Grid container justifyContent="flex-end" alignItems="center" mt={2}>
        <Stack direction="row" spacing={1}>
          {/* Reset */}
          <IconButton
            onClick={handleReset}
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
          >
            <RotateLeftIcon />
          </IconButton>

          {/* Create */}
          <IconButton
            onClick={handleSubmit}
            sx={{
              backgroundColor: isEdit ? "success.main" : "primary.main", // ✅ color change
              color: "white",
              "&:hover": {
                backgroundColor: isEdit ? "success.dark" : "primary.dark",
              },
            }}
          >
            {isEdit ? <UpdateIcon /> : <SaveIcon />} {/* ✅ icon change */}
          </IconButton>
        </Stack>
      </Grid>
    </motion.div>
  );
};

export default LeaveCard;
